import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';
import { Package, Settings, Edit, Trash2, Plus, Save, RefreshCw } from 'lucide-react';

export function AdminModules({ headers }) {
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planModules, setPlanModules] = useState([]);
  const [showEditPlanDialog, setShowEditPlanDialog] = useState(false);

  // Fetch modules and plans
  const fetchData = async () => {
    setLoading(true);
    try {
      const [modulesRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/modules`, { headers }),
        axios.get(`${API}/admin/plans`, { headers })
      ]);
      setModules(modulesRes.data.modules || []);
      setPlans(plansRes.data.items || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Seed modules
  const seedModules = async () => {
    try {
      await axios.post(`${API}/admin/modules/seed`, {}, { headers });
      toast.success('Modules seeded successfully');
      fetchData();
    } catch (e) {
      toast.error('Failed to seed modules');
    }
  };

  // Toggle module enabled
  const toggleModule = async (code, enabled) => {
    try {
      await axios.put(`${API}/admin/modules/${code}`, { enabled }, { headers });
      toast.success(`Module ${enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (e) {
      toast.error('Failed to update module');
    }
  };

  // Open plan module editor
  const openPlanEditor = async (plan) => {
    setSelectedPlan(plan);
    try {
      const res = await axios.get(`${API}/admin/plans/${plan.id}/modules`, { headers });
      setPlanModules(res.data.modules || []);
      setShowEditPlanDialog(true);
    } catch (e) {
      // Plan might not have modules set yet
      setPlanModules(plan.modules || []);
      setShowEditPlanDialog(true);
    }
  };

  // Toggle module in plan
  const togglePlanModule = (code) => {
    setPlanModules(prev => {
      if (prev.includes(code)) {
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
  };

  // Save plan modules
  const savePlanModules = async () => {
    if (!selectedPlan) return;
    
    try {
      await axios.put(`${API}/admin/plans/${selectedPlan.id}/modules`, {
        modules: planModules
      }, { headers });
      toast.success('Plan modules updated');
      setShowEditPlanDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update plan modules');
    }
  };

  // Group modules by category
  const groupedModules = modules.reduce((acc, m) => {
    const cat = m.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});

  const categoryLabels = {
    core: 'Core Modules',
    features: 'Premium Features',
    hardware: 'Hardware Integration',
    platform: 'Platform Access',
    security: 'Security Features'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Module Management</h2>
          <p className="text-muted-foreground">Control which features are available per subscription plan</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {modules.length === 0 && (
            <Button onClick={seedModules}>
              <Plus className="h-4 w-4 mr-2" />
              Seed Default Modules
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="modules">
        <TabsList>
          <TabsTrigger value="modules">
            <Package className="h-4 w-4 mr-2" />
            Modules ({modules.length})
          </TabsTrigger>
          <TabsTrigger value="plans">
            <Settings className="h-4 w-4 mr-2" />
            Plan Configuration ({plans.length})
          </TabsTrigger>
        </TabsList>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-6 mt-4">
          {Object.entries(groupedModules).map(([category, categoryModules]) => (
            <Card key={category}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{categoryLabels[category] || category}</CardTitle>
                <CardDescription>
                  {category === 'core' ? 'Always included in all plans' : 'Can be assigned to subscription plans'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categoryModules.map((module) => (
                    <div 
                      key={module.code}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        module.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{module.icon}</span>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {module.name}
                            {module.is_core && (
                              <Badge variant="secondary" className="text-xs">Core</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{module.description}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Code: <code className="bg-gray-100 px-1 rounded">{module.code}</code>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Count of plans with this module */}
                        <Badge variant="outline">
                          {plans.filter(p => (p.modules || []).includes(module.code)).length} plans
                        </Badge>
                        {!module.is_core && (
                          <Switch
                            checked={module.enabled}
                            onCheckedChange={(checked) => toggleModule(module.code, checked)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="mt-4">
          <div className="grid gap-4">
            {plans.map((plan) => {
              const planModuleCount = (plan.modules || []).length;
              const premiumModules = modules.filter(m => !m.is_core);
              
              return (
                <Card key={plan.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{plan.name}</h3>
                          {plan.is_default && (
                            <Badge variant="secondary">Default</Badge>
                          )}
                          {!plan.is_enabled && (
                            <Badge variant="destructive">Disabled</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {plan.cost_monthly > 0 ? (
                            <span>{plan.cost_monthly} {plan.currency}/month</span>
                          ) : (
                            <span>Free</span>
                          )}
                          {plan.subscriber_count > 0 && (
                            <span className="ml-2">• {plan.subscriber_count} subscribers</span>
                          )}
                        </div>
                        
                        {/* Module badges */}
                        <div className="flex flex-wrap gap-1 mt-3">
                          {(plan.modules || []).length === 0 ? (
                            <span className="text-sm text-muted-foreground italic">No premium modules assigned</span>
                          ) : (
                            (plan.modules || []).map(code => {
                              const mod = modules.find(m => m.code === code);
                              return mod ? (
                                <Badge key={code} variant="outline" className="text-xs">
                                  {mod.icon} {mod.name}
                                </Badge>
                              ) : null;
                            })
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold">
                            {planModuleCount + 2}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            modules
                          </div>
                        </div>
                        <Button onClick={() => openPlanEditor(plan)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Modules
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Plan Modules Dialog */}
      <Dialog open={showEditPlanDialog} onOpenChange={setShowEditPlanDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Modules for "{selectedPlan?.name}"
            </DialogTitle>
            <DialogDescription>
              Select which premium modules to include in this plan. Core modules are always included.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Core modules (always included) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">Core Modules (Always Included)</Label>
              <div className="grid gap-2">
                {modules.filter(m => m.is_core).map(module => (
                  <div 
                    key={module.code}
                    className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg opacity-60"
                  >
                    <Checkbox checked disabled />
                    <span className="text-lg">{module.icon}</span>
                    <span className="text-sm">{module.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium modules (selectable) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Premium Modules</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setPlanModules(modules.filter(m => !m.is_core).map(m => m.code))}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setPlanModules([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              <div className="grid gap-2">
                {modules.filter(m => !m.is_core && m.enabled).map(module => (
                  <div 
                    key={module.code}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      planModules.includes(module.code) 
                        ? 'bg-teal-50 border-teal-200' 
                        : 'bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => togglePlanModule(module.code)}
                  >
                    <Checkbox 
                      checked={planModules.includes(module.code)}
                      onCheckedChange={() => togglePlanModule(module.code)}
                    />
                    <span className="text-xl">{module.icon}</span>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{module.name}</div>
                      <div className="text-xs text-muted-foreground">{module.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPlanDialog(false)}>
              Cancel
            </Button>
            <Button onClick={savePlanModules}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
